import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuthStore } from '@/auth/authStore';
import { isStudyStudentExperience } from '@/pages/try/studyExperience/mode';
import {
  chemistryLabFrameSrc,
  chemistryLabNeedsSessionRestore,
  chemistryLabReturn,
} from './chemistryLabNavigation';
import { CHEMISTRY_LAB_RUNTIME } from './runtimeManifest';

const CHANNEL = 'airbotix-chemistry-lab-v1';
const READY_TIMEOUT_MS = 20000;

/** Independent Virtual Chemistry Lab, outside Study's subject-tool and account layouts. */
export function ChemistryLabPage() {
  const frame = useRef<HTMLIFrameElement>(null);
  const [params] = useSearchParams();
  const returnTo = chemistryLabReturn(params.get('from'), params.get('year'));
  const src = chemistryLabFrameSrc(
    CHEMISTRY_LAB_RUNTIME.entry,
    params.get('card'),
    params.get('view'),
  );
  const navigate = useNavigate();
  const [attempt, setAttempt] = useState(0);
  const [status, setStatus] = useState<'loading' | 'ready' | 'error'>('loading');
  const returnToAirbotix = useCallback(() => {
    // Public Lab reloads intentionally skip auth. Re-enter the protected app
    // through its normal bootstrap so HttpOnly sessions can restore both roles.
    // The fictional Study example has no real session: stay inside its own router.
    if (
      !isStudyStudentExperience() &&
      chemistryLabNeedsSessionRestore(returnTo, useAuthStore.getState().tokens)
    ) {
      window.location.assign(returnTo);
    } else {
      navigate(returnTo);
    }
  }, [navigate, returnTo]);

  useEffect(() => {
    if (status !== 'loading') return;
    const timer = window.setTimeout(() => setStatus('error'), READY_TIMEOUT_MS);
    return () => window.clearTimeout(timer);
  }, [attempt, status]);

  useEffect(() => {
    const receive = (event: MessageEvent) => {
      if (event.origin !== window.location.origin || event.source !== frame.current?.contentWindow)
        return;
      if (event.data?.channel !== CHANNEL) return;
      if (event.data.type === 'ready' || event.data.type === 'error') {
        setStatus(event.data.type);
      }
      if (event.data.type === 'exit') returnToAirbotix();
    };
    window.addEventListener('message', receive);
    return () => {
      window.removeEventListener('message', receive);
    };
  }, [returnToAirbotix]);

  return (
    <main
      className="fixed inset-0 z-50 h-[100dvh] overflow-hidden bg-canvas text-ink"
      aria-label="Airbotix Virtual Chemistry Lab"
    >
      <iframe
        key={attempt}
        ref={frame}
        src={src}
        title="Airbotix Virtual Chemistry Lab — 3D chemistry bench"
        className={`h-full w-full border-0 ${status === 'ready' ? '' : 'invisible'}`}
        tabIndex={status === 'ready' ? 0 : -1}
        allow="fullscreen"
        allowFullScreen
        onError={() => setStatus('error')}
      />
      {status !== 'ready' && (
        <div className="absolute inset-0 flex items-center justify-center bg-canvas p-5">
          <section
            className="w-full max-w-md rounded-3xl border border-hairline bg-canvas-pure p-6 shadow-card-soft"
            role={status === 'error' ? 'alert' : 'status'}
          >
            <h1 className="text-xl font-extrabold">
              {status === 'error' ? 'The Lab could not open' : 'Preparing your Virtual Chemistry Lab…'}
            </h1>
            <p className="mt-3 text-sm leading-relaxed text-ink-soft">
              {status === 'error'
                ? 'Check your connection, then try again.'
                : 'Opening the 3D chemistry bench and experiments.'}
            </p>
            <div className="mt-5 flex flex-wrap gap-3">
              {status === 'error' && (
                <button
                  className="btn-pill-primary"
                  onClick={() => {
                    setStatus('loading');
                    setAttempt((value) => value + 1);
                  }}
                >
                  Try again
                </button>
              )}
              <Link
                to={returnTo}
                className="btn-pill-secondary"
                onClick={(event) => {
                  if (event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;
                  event.preventDefault();
                  returnToAirbotix();
                }}
              >
                Back to Airbotix
              </Link>
            </div>
          </section>
        </div>
      )}
    </main>
  );
}
